import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { TrendingDown, TrendingUp, Minus, Info } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import api from '@/lib/api.ts'
import { useTenantStore } from '@/stores/tenants.ts'
import {
  Card,
  PageHeader,
  DataTable,
  EmptyState,
  LoadingState,
  ErrorState,
  StatusBadge,
  Tooltip,
  TooltipProvider,
} from '@/components/ui'

interface CustomerDso {
  bpId: string
  cardCode: string
  cardName: string
  tenantCode?: string | null
  openReceivables: number
  invoiceCount: number
  dso: number | null
  rating: 'good' | 'fair' | 'poor' | null
  currency: string
}

interface WccCompanyMetrics {
  totalReceivables: number
  dsoCompany: number | null
  totalPayables: number
  dpoCompany: number | null
  dioCompany: 0
  ccc: number | null
  currency: string
}

interface WccMetrics {
  customers: CustomerDso[]
  company: WccCompanyMetrics | null
  year: number
}

const fmt = (v: number, currency = 'EUR') =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v)

const fmtDays = (d: number | null) => (d === null ? '–' : `${Math.round(d)} Tage`)

function DsoRatingBadge({ rating }: { rating: 'good' | 'fair' | 'poor' | null }) {
  if (!rating) return <span className="text-gray-400 text-sm">–</span>
  const config = {
    good: { label: 'Gut', cls: 'text-green-700 bg-green-50 border-green-200' },
    fair: { label: 'Mittel', cls: 'text-yellow-700 bg-yellow-50 border-yellow-200' },
    poor: { label: 'Kritisch', cls: 'text-red-700 bg-red-50 border-red-200' },
  }[rating]
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${config.cls}`}>
      {config.label}
    </span>
  )
}

function MetricTile({
  label,
  value,
  sub,
  tooltip,
  highlight,
}: {
  label: string
  value: string
  sub?: string
  tooltip?: string
  highlight?: 'good' | 'neutral' | 'bad'
}) {
  const highlightCls =
    highlight === 'good'
      ? 'border-green-300 bg-green-50'
      : highlight === 'bad'
        ? 'border-red-300 bg-red-50'
        : 'border-gray-200 bg-gray-50'

  return (
    <div className={`rounded-lg p-5 border ${highlightCls}`}>
      <div className="flex items-center gap-1.5">
        <p className="text-small text-gray-500">{label}</p>
        {tooltip && (
          <TooltipProvider>
            <Tooltip content={tooltip}>
              <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <p className="text-h2 font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-small text-gray-500 mt-0.5">{sub}</p>}
    </div>
  )
}

function CccIndicator({ ccc }: { ccc: number | null }) {
  if (ccc === null) return <span className="text-gray-400">–</span>
  const rounded = Math.round(ccc)
  const isNegative = rounded < 0
  const isGood = rounded <= 20

  return (
    <div className="flex items-center gap-2">
      {isNegative ? (
        <TrendingDown className="w-5 h-5 text-green-600" />
      ) : isGood ? (
        <Minus className="w-5 h-5 text-yellow-500" />
      ) : (
        <TrendingUp className="w-5 h-5 text-red-500" />
      )}
      <span
        className={`text-h2 font-bold ${isNegative ? 'text-green-700' : isGood ? 'text-yellow-700' : 'text-red-700'}`}
      >
        {rounded} Tage
      </span>
    </div>
  )
}

const CUSTOMER_COLS: ColumnDef<CustomerDso>[] = [
  {
    id: 'rank',
    header: '#',
    size: 48,
    cell: ({ row }) => (
      <span className="text-gray-400 text-sm">{row.index + 1}</span>
    ),
  },
  {
    accessorKey: 'cardName',
    header: 'Kunde',
    cell: ({ row }) => (
      <div>
        <p className="font-medium text-gray-900">{row.original.cardName}</p>
        <p className="text-xs text-gray-400">{row.original.cardCode}</p>
      </div>
    ),
  },
  {
    accessorKey: 'tenantCode',
    header: 'Mandant',
    size: 100,
    cell: ({ row }) =>
      row.original.tenantCode ? (
        <div className="flex justify-center">
          <StatusBadge status={row.original.tenantCode} />
        </div>
      ) : (
        <span className="block text-center text-gray-400 text-xs">—</span>
      ),
  },
  {
    accessorKey: 'dso',
    header: 'DSO',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <span className="font-medium">{fmtDays(row.original.dso)}</span>
        <DsoRatingBadge rating={row.original.rating} />
      </div>
    ),
  },
  {
    accessorKey: 'openReceivables',
    header: 'Offene Forderungen',
    cell: ({ row }) =>
      row.original.openReceivables > 0
        ? <span className="font-medium text-orange-700">{fmt(row.original.openReceivables, row.original.currency)}</span>
        : <span className="text-gray-400">–</span>,
  },
  {
    accessorKey: 'invoiceCount',
    header: 'Offene Rechnungen',
    cell: ({ row }) => (
      <span className="text-gray-700">{row.original.invoiceCount}</span>
    ),
  },
]

export function WccDashboardPage() {
  const navigate = useNavigate()
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const { data, isLoading, isError, error } = useQuery<WccMetrics>({
    queryKey: ['wcc-metrics', activeTenantIds],
    queryFn: () => api.get('/business-partners/wcc-metrics').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  })

  const c = data?.company
  const year = data?.year ?? new Date().getFullYear()

  const cccHighlight: 'good' | 'neutral' | 'bad' | undefined =
    !c || c.ccc === null
      ? undefined
      : c.ccc < 0
        ? 'good'
        : c.ccc > 45
          ? 'bad'
          : 'neutral'

  return (
    <div className="space-y-6">
      <PageHeader
        title="Working Capital Cycle"
        description={`Kennzahlen DSO · DPO · DIO · CCC — Geschäftsjahr ${year}`}
      />

      {isLoading && <LoadingState label="Lade WCC-Kennzahlen …" />}
      {isError && (
        <ErrorState
          title="Fehler beim Laden"
          description={(error as Error)?.message ?? 'Unbekannter Fehler'}
          details={
            // Backend-Fehlermeldung aus Axios-Response anzeigen
            (() => {
              const axErr = error as { response?: { data?: { error?: string; stack?: string } } }
              const msg = axErr?.response?.data?.error
              const stack = axErr?.response?.data?.stack
              return [msg, stack].filter(Boolean).join('\n\n') || undefined
            })()
          }
        />
      )}

      {data && (
        <>
          {/* Unternehmenskennzahlen */}
          <Card>
            <Card.Header>
              <Card.Title>Unternehmen gesamt</Card.Title>
            </Card.Header>
            <Card.Body>
              {c ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <MetricTile
                    label="DSO – Debitorenlaufzeit"
                    value={fmtDays(c.dsoCompany)}
                    sub={`Forderungen: ${fmt(c.totalReceivables)}`}
                    tooltip="Days Sales Outstanding: Gewichtetes Ø-Alter offener Forderungen in Tagen. Berechnung: Σ(Offener Betrag × Tage seit Rechnungsdatum) ÷ Σ(Offener Betrag). Unabhängig vom Abrechnungsmonat."
                    highlight={
                      c.dsoCompany === null ? undefined : c.dsoCompany <= 30 ? 'good' : c.dsoCompany > 45 ? 'bad' : 'neutral'
                    }
                  />
                  <MetricTile
                    label="DPO – Kreditorenlaufzeit"
                    value={fmtDays(c.dpoCompany)}
                    sub={`Verbindlichkeiten: ${fmt(c.totalPayables)}`}
                    tooltip="Days Payable Outstanding: Gewichtetes Ø-Alter offener Verbindlichkeiten in Tagen. Berechnung: Σ(Offener Betrag × Tage seit Rechnungsdatum) ÷ Σ(Offener Betrag). Höher ist liquiditätsmäßig besser."
                  />
                  <MetricTile
                    label="DIO – Lagerreichweite"
                    value="0 Tage"
                    sub="Reines Dienstleistungsunternehmen"
                    tooltip="Days Inventory Outstanding: Bindezeit von Lagerware. Für implec GmbH als IT-Dienstleister strukturell 0."
                    highlight="good"
                  />
                  <div className={`rounded-lg p-5 border ${cccHighlight === 'good' ? 'border-green-300 bg-green-50' : cccHighlight === 'bad' ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center gap-1.5">
                      <p className="text-small text-gray-500">CCC – Cash Conversion Cycle</p>
                      <TooltipProvider>
                        <Tooltip content="Geldumschlagsdauer: Wie lange ist Kapital im operativen Geschäft gebunden? CCC = DIO + DSO − DPO. Negativ = exzellent (Kunden zahlen bevor Lieferanten bezahlt werden).">
                          <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="mt-1">
                      <CccIndicator ccc={c.ccc} />
                    </div>
                    <p className="text-small text-gray-500 mt-0.5">DIO + DSO − DPO</p>
                  </div>
                </div>
              ) : (
                <EmptyState
                  title="Keine Daten"
                  description="SAP nicht verfügbar oder kein Mandant konfiguriert."
                />
              )}
            </Card.Body>
          </Card>

          {/* Kunden-Ranking nach DSO */}
          <Card>
            <Card.Header>
              <Card.Title>DSO-Ranking Kunden</Card.Title>
              <p className="text-small text-gray-500 mt-0.5">
                Absteigende Sortierung — Kunden mit der längsten Debitorenlaufzeit zuerst
              </p>
            </Card.Header>
            <Card.Body className="p-0">
              <DataTable
                columns={CUSTOMER_COLS}
                data={data.customers}
                pagination={{ pageSize: 25 }}
                onRowClick={(row) => navigate(`/gp/${row.bpId}`)}
                emptyState={
                  <EmptyState
                    title="Keine Kunden mit Daten"
                    description="Für das laufende Jahr sind noch keine Rechnungs- und Forderungsdaten aus SAP vorhanden."
                  />
                }
              />
            </Card.Body>
          </Card>

          {/* Erklaerungsbox */}
          <Card>
            <Card.Header>
              <Card.Title>Was bedeuten diese Kennzahlen?</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                <div className="space-y-1">
                  <p className="font-semibold text-gray-900">DSO — Days Sales Outstanding</p>
                  <p>Wie lange sind Forderungen im Schnitt offen? Berechnung: gewichtetes Durchschnittsalter aller offenen Rechnungen (Σ offener Betrag × Tage offen ÷ Σ offener Betrag). Ein niedriger DSO (≤ 30 Tage) ist ideal.</p>
                </div>
                <div className="space-y-1">
                  <p className="font-semibold text-gray-900">DPO — Days Payable Outstanding</p>
                  <p>Wie lange dauert es, bis wir Lieferanten bezahlen? Ein höherer DPO ist liquiditätsmäßig vorteilhaft — wir nutzen den Lieferantenkredit.</p>
                </div>
                <div className="space-y-1">
                  <p className="font-semibold text-gray-900">DIO — Days Inventory Outstanding</p>
                  <p>Wie lange liegt Ware im Lager? Als IT-Dienstleister ohne nennenswerte Lagerbestände ist DIO strukturell 0.</p>
                </div>
                <div className="space-y-1">
                  <p className="font-semibold text-gray-900">CCC — Cash Conversion Cycle</p>
                  <p>Die Gesamtkennzahl: Wie viele Tage ist Kapital im Betrieb gebunden? CCC = DIO + DSO − DPO. Negativ bedeutet: Kunden zahlen, bevor wir Lieferanten bezahlen müssen — wie beim Amazon-Modell.</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  )
}
